/**
 * 音频转换工具
 * 
 * 注意：lamejs 库通过全局 script 标签加载（在 public/index.html 中）
 * 原因：lamejs 的 ES6 模块导出在 webpack 打包时存在内部依赖丢失问题（MPEGMode 等常量未定义）
 * 解决方案：使用预构建的 lame.min.js，通过全局变量 window.lamejs 访问
 */

/**
 * 将音频 Blob 转换为 MP3 格式
 * @param {Blob} audioBlob - 录音 Blob 对象
 * @returns {Promise<Blob>} MP3 格式的 Blob
 */
export async function convertToMP3(audioBlob) {
  try {
    // 1. 创建 AudioContext
    // 使用 16kHz 采样率：适合语音识别场景，减小文件大小
    const audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 16000
    });

    // 2. 读取 Blob 为 ArrayBuffer
    const arrayBuffer = await audioBlob.arrayBuffer();

    // 3. 解码音频数据
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // 4. 获取音频通道数据
    const channels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const samples = audioBuffer.length;

    // 5. 提取 PCM 数据（转换为 Int16）
    let leftChannel, rightChannel;
    if (channels === 2) {
      leftChannel = audioBuffer.getChannelData(0);
      rightChannel = audioBuffer.getChannelData(1);
    } else {
      leftChannel = audioBuffer.getChannelData(0);
      rightChannel = leftChannel; // 单声道复制到右声道
    }

    // 将 Float32Array 转换为 Int16Array
    const left = floatTo16BitPCM(leftChannel);
    const right = floatTo16BitPCM(rightChannel);

    // 6. 使用 lamejs 编码为 MP3
    // 使用 64kbps 码率：适合语音内容，减小文件大小
    const mp3encoder = new window.lamejs.Mp3Encoder(channels, sampleRate, 64);
    const mp3Data = [];

    const sampleBlockSize = 1152; // lamejs 推荐的块大小
    for (let i = 0; i < samples; i += sampleBlockSize) {
      const leftChunk = left.subarray(i, i + sampleBlockSize);
      const rightChunk = right.subarray(i, i + sampleBlockSize);
      const mp3buf = mp3encoder.encodeBuffer(leftChunk, rightChunk);
      if (mp3buf.length > 0) {
        mp3Data.push(mp3buf);
      }
    }

    // 写入剩余数据
    const mp3buf = mp3encoder.flush();
    if (mp3buf.length > 0) {
      mp3Data.push(mp3buf);
    }

    // 7. 创建 MP3 Blob
    const mp3Blob = new Blob(mp3Data, { type: 'audio/mp3' });

    console.log('MP3 转换完成', {
      originalSize: audioBlob.size,
      mp3Size: mp3Blob.size,
      sampleRate: sampleRate,
      channels: channels,
      bitrate: '64kbps'
    });

    return mp3Blob;
  } catch (error) {
    console.error('MP3 转换失败:', error);
    throw new Error('音频格式转换失败');
  }
}

/**
 * 将 Float32Array 转换为 Int16Array
 * @param {Float32Array} float32Array
 * @returns {Int16Array}
 */
function floatTo16BitPCM(float32Array) {
  const int16Array = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return int16Array;
}

